import React from "react";

import { BlockStack, InlineGrid, Text, Select, RangeSlider, Checkbox } from "@shopify/polaris";

import { ColorField, PxField } from "./fields";

import {

  ANIMATION_TYPE_OPTIONS,

  defaultDurationForType,

  isMessageAnimationEnabled,

  isTickerAnimation,

  MESSAGE_ANIMATION_TYPES,

  resolveMessageAnimation,

  TICKER_DURATION_DEFAULT,

  TICKER_DURATION_MAX,

  TICKER_DURATION_MIN,

} from "../../utils/messageAnimation";



const FONT_OPTIONS = [

  { label: "Use your theme fonts", value: "theme" },

  { label: "System sans-serif", value: "sans" },

  { label: "System serif", value: "serif" },

];



export default function DesignSection({ design, placementType, onPatch }) {

  const d = design || {};

  const card = d.card || {};

  const sp = card.spacing || {};

  const pb = d.progress_bar || {};

  const typo = d.typography || {};

  const btn = d.button || {};

  const icon = d.icon || {};

  const layout = d.layout || {};

  const animRaw = d.message_animation || {};

  const animEnabled = isMessageAnimationEnabled(d);

  const activeAnim = resolveMessageAnimation(d);

  const animType = activeAnim?.type || animRaw.type || MESSAGE_ANIMATION_TYPES.TICKER_RTL;

  const showTickerSpeed = animEnabled && isTickerAnimation(d);



  const showWidgetWidth =

    placementType === "product_page" || placementType === "cart_page" || placementType === "home_page";



  const widthHelpText =

    placementType === "product_page"

      ? "Width relative to the product info / add-to-cart column on the storefront."

      : placementType === "home_page"

        ? "100% spans the full browser width edge-to-edge. Lower values stay centered in the page content area."

        : "100% matches the cart items / form column width. Lower values stay centered in that area.";



  const patchCard = (p) => onPatch({ card: p });

  const patchSpacing = (p) => onPatch({ card: { spacing: p } });

  const patchPb = (p) => onPatch({ progress_bar: p });

  const patchTypo = (p) => onPatch({ typography: p });

  const patchBtn = (p) => onPatch({ button: p });



  const patchAnimation = (patch) => {

    onPatch({

      message_animation: {

        ...animRaw,

        ...patch,

      },

    });

  };



  const typoRow = (key, label) => {

    const t = typo[key] || {};

    return (

      <InlineGrid columns={{ xs: 1, sm: 2 }} gap="300">

        <PxField

          label={`${label} size`}

          value={t.size ?? 16}

          onChange={(v) => patchTypo({ [key]: { ...t, size: v } })}

          max={48}

        />

        <ColorField

          label={`${label} color`}

          value={t.color}

          onChange={(v) => patchTypo({ [key]: { ...t, color: v } })}

        />

      </InlineGrid>

    );

  };



  return (

    <BlockStack gap="500">

      {showWidgetWidth ? (

        <BlockStack gap="300">

          <Text as="h3" variant="headingSm">

            Layout

          </Text>

          <RangeSlider

            label="Widget width"

            value={layout.widget_width_percent ?? 100}

            min={40}

            max={100}

            step={5}

            output

            suffix="%"

            onChange={(v) => onPatch({ layout: { widget_width_percent: v } })}

            helpText={widthHelpText}

          />

        </BlockStack>

      ) : null}



      <BlockStack gap="300">

        <Text as="h3" variant="headingSm">

          Message animation

        </Text>

        <Checkbox

          label="Enable message animation"

          checked={animEnabled}

          onChange={(checked) => {

            if (checked) {

              const type = animRaw.type || MESSAGE_ANIMATION_TYPES.TICKER_RTL;

              patchAnimation({

                enabled: true,

                type,

                duration_sec: animRaw.duration_sec ?? defaultDurationForType(type),

              });

            } else {

              patchAnimation({ enabled: false });

            }

          }}

          helpText="When off, the bar shows a static message even if an animated template is selected."

        />

        {animEnabled ? (

          <BlockStack gap="300">

            <Select

              label="Animation style"

              options={ANIMATION_TYPE_OPTIONS}

              value={animType}

              onChange={(type) =>

                patchAnimation({

                  enabled: true,

                  type,

                  duration_sec: defaultDurationForType(type),

                })

              }

            />

            {showTickerSpeed ? (

              <RangeSlider

                label="Ticker scroll speed"

                value={Number(activeAnim?.duration_sec) || TICKER_DURATION_DEFAULT}

                min={TICKER_DURATION_MIN}

                max={TICKER_DURATION_MAX}

                step={1}

                output

                suffix="sec"

                onChange={(v) => patchAnimation({ enabled: true, type: animType, duration_sec: v })}

                helpText="One full pass from right to left. Lower seconds = faster scroll."

              />

            ) : null}

          </BlockStack>

        ) : null}

      </BlockStack>



      <BlockStack gap="300">

        <Text as="h3" variant="headingSm">

          Card

        </Text>

        <InlineGrid columns={{ xs: 1, sm: 2 }} gap="300">

          <PxField label="Corner radius" value={card.corner_radius ?? 8} onChange={(v) => patchCard({ corner_radius: v })} max={60} />

          <PxField label="Border size" value={card.border_size ?? 1} onChange={(v) => patchCard({ border_size: v })} max={20} />

          <ColorField label="Border color" value={card.border_color} onChange={(v) => patchCard({ border_color: v })} />

        </InlineGrid>

        <InlineGrid columns={{ xs: 1, sm: 2 }} gap="300">

          <PxField label="Inside top" value={sp.inside_top ?? 16} onChange={(v) => patchSpacing({ inside_top: v })} />

          <PxField label="Inside bottom" value={sp.inside_bottom ?? 16} onChange={(v) => patchSpacing({ inside_bottom: v })} />

          <PxField label="Outside top" value={sp.outside_top ?? (placementType === "home_page" ? 0 : 20)} onChange={(v) => patchSpacing({ outside_top: v })} />

          <PxField label="Outside bottom" value={sp.outside_bottom ?? (placementType === "home_page" ? 0 : 20)} onChange={(v) => patchSpacing({ outside_bottom: v })} />

        </InlineGrid>

      </BlockStack>



      <BlockStack gap="300">

        <Text as="h3" variant="headingSm">

          Progress bar

        </Text>

        <InlineGrid columns={{ xs: 1, sm: 2 }} gap="300">

          <ColorField label="Track color" value={pb.background} onChange={(v) => patchPb({ background: v })} />

          <ColorField label="Progress bar color" value={pb.fill} onChange={(v) => patchPb({ fill: v })} />

          <PxField label="Corner radius" value={pb.corner_radius ?? 4} onChange={(v) => patchPb({ corner_radius: v })} max={40} />

        </InlineGrid>

      </BlockStack>



      <BlockStack gap="300">

        <Text as="h3" variant="headingSm">

          Typography

        </Text>

        <Select

          label="Font"

          options={FONT_OPTIONS}

          value={typo.font || "theme"}

          onChange={(v) => patchTypo({ font: v })}

        />

        {typoRow("message", "Message")}

        {typoRow("product_title", "Product title")}

        {typoRow("product_price", "Product price")}

        {typoRow("compare_price", "Compare at price")}

      </BlockStack>



      <BlockStack gap="300">

        <Text as="h3" variant="headingSm">

          Button

        </Text>

        <InlineGrid columns={{ xs: 1, sm: 2 }} gap="300">

          <ColorField label="Button border color" value={btn.border_color} onChange={(v) => patchBtn({ border_color: v })} />

          <ColorField label="Button text color" value={btn.text_color} onChange={(v) => patchBtn({ text_color: v })} />

          <PxField label="Button text size" value={btn.text_size ?? 14} onChange={(v) => patchBtn({ text_size: v })} max={32} />

          <PxField label="Corner radius" value={btn.corner_radius ?? 4} onChange={(v) => patchBtn({ corner_radius: v })} max={40} />

        </InlineGrid>

      </BlockStack>



      <BlockStack gap="300">

        <Text as="h3" variant="headingSm">

          Icon

        </Text>

        <ColorField label="Arrow icon color" value={icon.arrow_color} onChange={(v) => onPatch({ icon: { arrow_color: v } })} />

      </BlockStack>

    </BlockStack>

  );

}


